// "Add to Apple Music" on the fan playlist page (/playlist/:receiptId).
//
// Apple has no Spotify-style public-link path, so the flow is inverted: the
// FAN authorizes their own Apple Music account (Apple's popup) and the
// playlist is created directly in their library from the catalog ids the
// server resolved (spotifyId → ISRC → exact recording — no fuzzy search).
//
// MusicKit JS v3 is loaded on demand — this page only — and used ONLY for
// configure() + authorize() (which yields the Music-User-Token). The
// playlist itself is created with plain fetch against the documented Apple
// Music REST API (api.music.apple.com is CORS-open: verified
// access-control-allow-origin:* with POST + music-user-token headers).
// Keeping the writes on the documented REST surface means no dependency on
// MusicKit's undocumented wrapper quirks.
//
// iPhone Safari popup rule: authorize() must run inside the tap's user
// gesture. So the page configures MusicKit as soon as it has the developer
// token (page load) and only SHOWS the button once that's done — the tap
// handler then goes straight to authorize().

declare global {
  interface Window {
    MusicKit?: any;
  }
}

const MUSICKIT_SRC = "https://js-cdn.music.apple.com/musickit/v3/musickit.js";
const API_BASE = "https://api.music.apple.com";
const ADD_TRACKS_CHUNK = 100;

let loadPromise: Promise<any> | null = null;

function loadMusicKit(): Promise<any> {
  if (window.MusicKit) return Promise.resolve(window.MusicKit);
  if (!loadPromise) {
    loadPromise = new Promise((resolve, reject) => {
      // v3 fires "musickitloaded" on document once window.MusicKit exists.
      document.addEventListener(
        "musickitloaded",
        () => (window.MusicKit ? resolve(window.MusicKit) : reject(new Error("musickit-missing"))),
        { once: true }
      );
      const s = document.createElement("script");
      s.src = MUSICKIT_SRC;
      s.async = true;
      s.onerror = () => {
        loadPromise = null; // a network blip shouldn't wedge the page forever
        reject(new Error("musickit-script-failed"));
      };
      document.head.appendChild(s);
    });
  }
  return loadPromise;
}

/**
 * Load + configure MusicKit with our developer token. Returns the MusicKit
 * instance, or throws (the page hides the button — same as unconfigured).
 * configure() validates the token with Apple, so a resolved instance means
 * a tap can go straight to authorize().
 */
export async function prepareMusicKit(developerToken: string): Promise<any> {
  const MusicKit = await loadMusicKit();
  const configured = await MusicKit.configure({
    developerToken,
    app: { name: "Request App", build: "1" },
  });
  return configured || MusicKit.getInstance();
}

export type AppleAddErrorCode = "declined" | "no-subscription" | "failed";

export class AppleAddError extends Error {
  code: AppleAddErrorCode;
  constructor(code: AppleAddErrorCode) {
    super(code);
    this.code = code;
  }
}

async function appleFetch(
  path: string,
  developerToken: string,
  userToken: string,
  body: unknown
): Promise<any> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${developerToken}`,
      "Music-User-Token": userToken,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (res.status === 403) throw new AppleAddError("no-subscription");
  if (!res.ok) throw new AppleAddError("failed");
  // Apple answers some writes with 202/204 and an empty body.
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

// Verified live: when MusicKit's popup can't open or complete
// (popup blocker, abandoned window), authorize() neither resolves NOR
// rejects — it hangs forever, which would wedge the button at "Adding…".
// The watchdog turns that into the declined note. A fan who finishes
// Apple's sign-in after the deadline isn't stranded: MusicKit keeps the
// session, so their next tap authorizes instantly and the add proceeds.
const AUTHORIZE_DEADLINE_MS = 90_000;

/**
 * The tap handler's whole job: authorize the fan, create the playlist in
 * THEIR library, add the resolved tracks in chunks. Throws AppleAddError
 * with an honest code; anything thrown by Apple's popup counts as declined.
 */
export async function addSetlistToAppleMusic(opts: {
  music: any;
  developerToken: string;
  name: string;
  description: string;
  appleMusicIds: Array<string | null | undefined>;
  authorizeDeadlineMs?: number;
}): Promise<{ added: number }> {
  const ids = opts.appleMusicIds.filter(Boolean) as string[];
  if (ids.length === 0) throw new AppleAddError("failed");

  let userToken: string;
  let deadline: ReturnType<typeof setTimeout> | null = null;
  try {
    userToken = await Promise.race([
      opts.music.authorize(),
      new Promise<never>((_, reject) => {
        deadline = setTimeout(
          () => reject(new AppleAddError("declined")),
          opts.authorizeDeadlineMs ?? AUTHORIZE_DEADLINE_MS
        );
      }),
    ]);
  } catch {
    throw new AppleAddError("declined");
  } finally {
    if (deadline) clearTimeout(deadline);
  }
  if (!userToken) throw new AppleAddError("declined");

  const created = await appleFetch("/v1/me/library/playlists", opts.developerToken, userToken, {
    attributes: { name: opts.name, description: opts.description },
  });
  const playlistId = created?.data?.[0]?.id;
  if (!playlistId) throw new AppleAddError("failed");

  let added = 0;
  for (let i = 0; i < ids.length; i += ADD_TRACKS_CHUNK) {
    const chunk = ids.slice(i, i + ADD_TRACKS_CHUNK);
    const body = { data: chunk.map((id) => ({ id, type: "songs" })) };
    try {
      await appleFetch(
        `/v1/me/library/playlists/${playlistId}/tracks`,
        opts.developerToken,
        userToken,
        body
      );
      added += chunk.length;
    } catch (err) {
      // One retry per chunk, then move on — a transient blip must not void
      // the 90 tracks that already landed.
      try {
        await appleFetch(
          `/v1/me/library/playlists/${playlistId}/tracks`,
          opts.developerToken,
          userToken,
          body
        );
        added += chunk.length;
      } catch {
        /* this chunk is lost; keep going */
      }
    }
  }
  if (added === 0) throw new AppleAddError("failed");
  return { added };
}
